import { CommonApiResponse } from "../../Controllers/Types/ApiResponse";
import { PostInspectionList, SiteVisitReference } from "../../Controllers/Types/SiteVisit/SiteVisitTypes";
import apiClient from "../apiClient";
import { baseURL } from "../ApiConstant";

// Get Site visit data
export const getSiteVisitByFolderId = async (folder_id: number) => {
    try {
        const response = await apiClient.get(`/api/site-visit/list/${folder_id}`);

        if (response.status === 200) {
            return { status: true, body: response.data };
        } else {
            return { status: false, error: "failed" };
        }
    } catch (error: any) {
        return {
            status: false,
            error: "An error occurred. Please try again later.",
        };
    }
};

/* **************************** Architect **************************** */
// Get all inspections list
export const getInspectionList = async () => {
    try {
        const response = await apiClient.get(`/api/site-visit/inspection-list`);

        if (response.status === 200) {
            return { status: true, body: response.data };
        } else {
            return { status: false, error: "failed" };
        }
    } catch (error: any) {
        return {
            status: false,
            error: "An error occurred. Please try again later.",
        };
    }
};

// Create new site visit request
export const createSiteVisit = async (
    folder_id: number,
    status: 'Draft' | 'Pending',
    date: string,
    note: string | null,
    inspection_list: PostInspectionList[]
): Promise<CommonApiResponse> => {
    try {
        // prepare ids for number list
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const response = await fetch(`${baseURL}/api/site-visit/architecture/create`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: JSON.stringify({
                folder_id,
                status,
                date,
                note,
                inspection_list,
            }),
        });

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Update existing site visit request
export const updateSiteVisit = async (
    site_visit_id: number,
    status: string,
    date: string,
    note: string | null,
    inspection_list: PostInspectionList[]
): Promise<CommonApiResponse> => {
    try {
        // prepare ids for number list
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const response = await fetch(`${baseURL}/api/site-visit/architecture/update/${site_visit_id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: JSON.stringify({
                status,
                date,
                note,
                inspection_list,
            }),
        });

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete site visit request
export const deleteSiteVisit = async (
    site_visit_id: number
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(
            `${baseURL}/api/site-visit/architecture/delete/${site_visit_id}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
            }
        );

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return { status: false, statusCode: 422, massage: "Body error" };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Create/Update response for site visit note by architect
export const siteVisitResponseByArchitect = async (
    site_visit_id: number,
    note_id: number,
    note_response: string,
    date: string,
    file_list: File[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        /* create form data */
        const formData = new FormData();
        formData.append("site_visit_id", String(site_visit_id));
        formData.append("note_id", String(note_id));
        formData.append("response", note_response);
        formData.append("date", date);

        if (file_list && file_list.length > 0) {
            file_list.forEach((file) => {
                formData.append("file_list[]", file);
            });
        }

        const response = await fetch(
            `${baseURL}/api/site-visit/architecture/note-response-create-update`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
                body: formData,
            }
        );
        const data = await response.json();
        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete architect response
export const deleteArchitectResponse = async (
    response_id: number
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(
            `${baseURL}/api/site-visit/architecture/note-response-delete/${response_id}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
            }
        );

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return { status: false, statusCode: 422, massage: "Body error" };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete response file
export const deleteArcResponseFile = async (
    file_id: number
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(
            `${baseURL}/api/site-visit/architecture/note-response-image-delete/${file_id}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
            }
        );

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return { status: false, statusCode: 422, massage: "Body error" };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};
/* ******************************************************************* */


/* *********************** Building Inspector ************************ */
// Get all inspections time list
export const getInspectionTimeList = async () => {
    try {
        const response = await apiClient.get(`/api/site-visit/time-list`);

        if (response.status === 200) {
            return { status: true, body: response.data };
        } else {
            return { status: false, error: "failed" };
        }
    } catch (error: any) {
        return {
            status: false,
            error: "An error occurred. Please try again later.",
        };
    }
};

// Create note
export const createSiteVisitNote = async (
    site_visit_id: number,
    inspection_list_id: number,
    reference_id: number | null,
    note_no: string,
    note: string,
    status: string,
    file_list: File[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        /* create form data */
        const formData = new FormData();
        formData.append("site_visit_id", String(site_visit_id));
        formData.append("inspection_list_id", String(inspection_list_id));

        if (reference_id) {
            formData.append("reference_id", String(reference_id));
        }

        formData.append("note_no", note_no);
        formData.append("note", note);
        formData.append("status", status);

        if (file_list && file_list.length > 0) {
            file_list.forEach((file) => {
                formData.append("file_list[]", file);
            });
        }

        const response = await fetch(
            `${baseURL}/api/site-visit/bi/note-create`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
                body: formData,
            }
        );
        const data = await response.json();
        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Add muliple inspection references
export const addNoteReferenceList = async (
    site_visit_id: number,
    inspection_list_id: number,
    reference_list: SiteVisitReference[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        /* create form data */
        const formData = new FormData();
        formData.append("site_visit_id", String(site_visit_id));
        formData.append("inspection_list_id", String(inspection_list_id));

        if (reference_list && reference_list.length > 0) {
            reference_list.forEach((reference) => {
                formData.append("reference_list[]", String(reference?.id));
            });
        }

        const response = await fetch(
            `${baseURL}/api/site-visit/inspection-reference-list-add`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
                body: formData,
            }
        );
        const data = await response.json();
        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Update note
export const updateSiteVisitNote = async (
    note_id: number,
    site_visit_id: number,
    reference_id: number | null,
    note: string,
    status: string,
    file_list: File[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        /* create form data */
        const formData = new FormData();
        formData.append("site_visit_id", String(site_visit_id));

        if (reference_id) {
            formData.append("reference_id", String(reference_id));
        }

        formData.append("note", note);
        formData.append("status", status);

        if (file_list && file_list.length > 0) {
            file_list.forEach((file) => {
                formData.append("file_list[]", file);
            });
        }

        const response = await fetch(
            `${baseURL}/api/site-visit/bi/note-update/${note_id}`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
                body: formData,
            }
        );
        const data = await response.json();
        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete note
export const deleteSiteVisitNote = async (
    note_id: number
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(
            `${baseURL}/api/site-visit/bi/note-delete/${note_id}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
            }
        );

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return { status: false, statusCode: 422, massage: "Body error" };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete note file
export const deleteSiteVisitNoteFile = async (
    file_id: number
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(
            `${baseURL}/api/site-visit/bi/note-image-delete/${file_id}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
            }
        );

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return { status: false, statusCode: 422, massage: "Body error" };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Sign report by building inspector
export const signSiteVisitReportBi = async (
    site_visit_id: number,
    sign: string,
    email_list?: string[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const response = await fetch(
            `${baseURL}/api/site-visit/bi/sign-report`,
            {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
                body: JSON.stringify({
                    site_visit_id,
                    sign,
                    ...(email_list && email_list.length > 0 ? { email_list } : {})
                }),
            }
        );

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Send report via email
export const sendSiteVisitReportViaEmail = async (
    site_visit_id: number,
    email_list: string[]
): Promise<CommonApiResponse> => {
    try {
        // prepare ids for number list
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const response = await fetch(`${baseURL}/api/site-visit/bi/report-email-send`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: JSON.stringify({
                site_visit_id,
                ...(email_list && email_list.length > 0 ? { email_list } : {})
            }),
        });

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Accept site visit
export const acceptSiteVisit = async (
    site_visit_id: number,
    time_id: number,
    date: string
): Promise<CommonApiResponse> => {
    try {
        // prepare ids for number list
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const response = await fetch(`${baseURL}/api/site-visit/building-inspector/status-change/${site_visit_id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: JSON.stringify({
                time_id,
                date
            }),
        });

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};
/* ******************************************************************* */